import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Card,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  generateOtpRequest,
  verifyOtpRequest,
  verifyOrderRequest,
} from "../../redux/OfflineOrder/offlineOrderSlice";
import {
  createOfflineSipPaymentRequest,
  createOfflineSipInstallmentRequest,
  resetCreatedSipOrder,
} from "../../redux/Sip/sipSlice";
import { useAppTheme } from "../../theme/useAppTheme";

const OTP_LENGTH = 4;

const FromStoreTab = ({
  storeList,
  offlineOrderData,
  orderType = "BUY",
  onVerified,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  console.log("offlineOrderData123", offlineOrderData);

  const { otpLoading, otpSuccess, otpVerified, verifyOrderLoading, error } =
    useSelector((state) => state.offline_order);

  const themeColors = useAppTheme();

  const {
    loading: sipLoading,
    error: sipError,
    createdSipOrder,
  } = useSelector((state) => state.sip || {});
  console.log("createdSipOrder", createdSipOrder);

  const [selectedStore, setSelectedStore] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [otp, setOtp] = useState("");
  const [otpScreen, setOtpScreen] = useState(false);
  const [timer, setTimer] = useState(0);

  const [loadingOtp, setLoadingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const [openSheet, setOpenSheet] = useState(false);

  const otpInputRef = useRef(null);
  const purchaseHandled = useRef(false);

  // When entering SIP/installment flow, clear local OTP UI state so we don't show stale "OTP sent" screen
  useEffect(() => {
    if (orderType === "SIP") {
      setOtpScreen(false);
      setOtp("");
    }
  }, [orderType]);

  const filteredStores = (storeList || [])
    .filter((s) => s && s.branchName)
    .filter(
      (s) =>
        s.branchName.toLowerCase().includes((searchTerm || "").toLowerCase()) ||
        s.city.toLowerCase().includes((searchTerm || "").toLowerCase()) ||
        s.pincode.toLowerCase().includes((searchTerm || "").toLowerCase()),
    );

  useEffect(() => {
    if (timer === 0) return;

    const interval = setInterval(() => {
      setTimer((t) => (t > 0 ? t - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  const generateOtp = () => {
    if (!selectedStore?.phone) return;
    setLoadingOtp(true);

    dispatch(
      generateOtpRequest({
        otpSrc: selectedStore?.phone,
        otpFor: "Customer",
        otpType: orderType,
        jeweller: process.env.REACT_APP_JEWELLER_ID,
      }),
    );
  };

  useEffect(() => {
    if (otpLoading) return;
    if (otpSuccess) {
      // For SIP/installment flow only show OTP screen if user actually generated OTP in this session (has selectedStore)
      if (orderType === "SIP" && !selectedStore) return;
      setOtpScreen(true);
      setLoadingOtp(false);
      setTimer(30);
      setOtp("");

      setTimeout(() => {
        if (otpInputRef.current) otpInputRef.current.focus();
      }, 100);
    }

    if (error) {
      setLoadingOtp(false);
    }
  }, [otpSuccess, otpLoading, error, orderType, selectedStore]);

  useEffect(() => {
    if (otpScreen && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [otpScreen]);

  const verifyOtp = async () => {
    if (otp.length !== OTP_LENGTH) return;

    setVerifyingOtp(true);

    dispatch(
      verifyOtpRequest({
        otpSrc: selectedStore?.phone,
        otp: Number(otp),
        otpType: orderType,
        otpFor: "Customer",
        jeweller: process.env.REACT_APP_JEWELLER_ID,
      }),
    );
  };

  useEffect(() => {
    if (!otpVerified) return;

    setVerifyingOtp(false);

    // For SIP/installment, do not create order unless user selected a store (and thus went through OTP on this screen)
    if (orderType === "SIP" && !selectedStore) return;

    if (orderType === "PURCHASE") {
      if (!selectedStore?.branchCode || purchaseHandled.current) return;
      purchaseHandled.current = true;
      onVerified?.(selectedStore);
      return;
    }

    if (orderType === "BUY") {
      dispatch(
        verifyOrderRequest({
          orderId: offlineOrderData.orderId,
          branchCode: selectedStore?.branchCode,
        }),
      );

      navigate("/payment-status", {
        state: {
          metalName: offlineOrderData.metalName,
          metalPurity: offlineOrderData.purity,
          karat: offlineOrderData.karat,
          metalWeight: offlineOrderData.weight,
          orderId: offlineOrderData.orderId,
          isSuccess: true,
          amount: offlineOrderData.finalAmount,
          invoiceUrl: `${process.env.REACT_APP_INVOICE_URL}/invoice/${offlineOrderData.orderId}`,
        },
      });
    }

    if (orderType === "SIP") {
      if (offlineOrderData.isInstallment) {
        const pendingInstallment =
          offlineOrderData?.moneysipplan?.sipInstallments?.find(
            (i) => i.status === "PENDING",
          );
        const pendingInstallmentId = pendingInstallment?._id;
        const payload = {
          paymentMode: "Offline",
          id: pendingInstallmentId,
          sipInstallment: {
            paymentMode: "Offline",
            branchName: selectedStore?.branchName,
            branchCode: selectedStore?.branchCode,
            phone: selectedStore?.phone,
            installmentDate: offlineOrderData.installmentDate || Date.now(),
            totalPaidAmount: 0.0,
          },
          orderId: offlineOrderData.orderId || "",
          final_amount: offlineOrderData.final_amount,
          weight: offlineOrderData.weight,
          installment_amount: offlineOrderData.installment_amount,
        };
        console.log("Installment Payload:", payload);
        dispatch(createOfflineSipInstallmentRequest(payload));
      } else {
        const payload = {
          sipPlan: {
            sipInstallments: [
              {
                paymentMode: "Offline",
                branchName: selectedStore?.branchName,
                branchCode: selectedStore?.branchCode,
                phone: selectedStore?.phone,
                installmentDate: offlineOrderData.installmentDate
                  ? Number(offlineOrderData.installmentDate)
                  : new Date().getDate(),
                totalPaidAmount: 0.0,
              },
            ],
            metalName: offlineOrderData.metalName,
            noOfMonths: offlineOrderData.noOfMonths,
            installmentAmount: offlineOrderData.installment_amount,
            purity: offlineOrderData.purity,
            installmentDate: offlineOrderData.installmentDate || 0,
            bullion: offlineOrderData.bullion,
            moneysipplan: offlineOrderData.moneysipplan,
            claimed: "",
            tag: offlineOrderData.tag || "",
          },
          orderId: offlineOrderData.orderId,
          final_amount: offlineOrderData.final_amount,
          weight: offlineOrderData.weight,
          installment_amount: offlineOrderData.installment_amount,
          tag: offlineOrderData.tag || "",
        };

        console.log("Payload:", payload);
        dispatch(createOfflineSipPaymentRequest(payload));
      }
    }
  }, [otpVerified]);

  useEffect(() => {
    if (orderType === "SIP" && createdSipOrder?.success) {
      navigate("/payment-status", {
        state: {
          metalName: offlineOrderData.metalName,
          metalPurity: offlineOrderData.purity,
          karat: offlineOrderData.karat,
          metalWeight: offlineOrderData.weight,
          orderId: createdSipOrder.orderId || offlineOrderData.orderId,
          isSuccess: true,
          amount: offlineOrderData.final_amount,
          invoiceUrl: `${process.env.REACT_APP_INVOICE_URL}/invoice/${createdSipOrder.orderId || offlineOrderData.orderId}`,
        },
      });
      dispatch(resetCreatedSipOrder());
    }
  }, [createdSipOrder, orderType]);

  return (
    <Box sx={{ padding: 2 }}>
      {!selectedStore && (
        <Card
          sx={{
            padding: 2,
            cursor: "pointer",
            borderRadius: 2,
            display: "flex",
            justifyContent: "space-between",
            background: themeColors.mediumContainer,
            color: themeColors.text,
            border: `1px solid ${themeColors.scaffold}`,
          }}
          onClick={() => setOpenSheet(true)}
        >
          <Typography>Select Store</Typography>
          <Typography>▶</Typography>
        </Card>
      )}

      {selectedStore && (
        <Card
          sx={{
            padding: 2,
            borderRadius: 2,
            marginBottom: 2,
            background: themeColors.mediumContainer,
            color: themeColors.text,
            border: `1px solid ${themeColors.scaffold}`,
          }}
        >
          <Typography variant="h6">{selectedStore?.branchName}</Typography>
          <Typography>Phone: {selectedStore?.phone}</Typography>
          <Typography>Code: {selectedStore?.branchCode}</Typography>
          <Typography>
            Address:{" "}
            {`${selectedStore.address1 || ""} ${selectedStore.address2 || ""} ${
              selectedStore.city || ""
            } - ${selectedStore.pincode}`}
          </Typography>

          <Button
            sx={{
              mt: 2,
              background: themeColors.primary,
              color: themeColors.text,
              "&:hover": {
                background: themeColors.svgColor,
              },
              border: `1px solid ${themeColors.scaffold}`,
            }}
            variant="outlined"
            onClick={() => setOpenSheet(true)}
          >
            Change Store
          </Button>
        </Card>
      )}

      {selectedStore && !otpScreen && (
        <Button
          variant="contained"
          fullWidth
          disabled={loadingOtp}
          onClick={generateOtp}
          sx={{
            background: themeColors.primary,
            color: themeColors.text,
            "&:hover": {
              background: themeColors.svgColor,
            },
            border: `1px solid ${themeColors.scaffold}`,
            fontWeight: "bold",
            py: 1.5,
            fontSize: "1rem",
            textTransform: "none",
          }}
        >
          {loadingOtp ? <CircularProgress size={24} /> : "Generate OTP"}
        </Button>
      )}

      {otpScreen && (
        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Typography>
            OTP sent to store admin number: {selectedStore?.phone}
          </Typography>

          <Typography
            sx={{
              mt: 1,
              textDecoration: timer === 0 ? "underline" : "none",
              cursor: timer === 0 ? "pointer" : "default",
            }}
            onClick={timer === 0 ? generateOtp : null}
          >
            {timer > 0 ? `${timer} seconds` : "Resend OTP"}
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 1.5,
              mt: 3,
              cursor: "text",
              "@keyframes otpCaretBlink": {
                "0%, 49%": { opacity: 1 },
                "50%, 100%": { opacity: 0 },
              },
            }}
            onClick={() => otpInputRef.current?.focus()}
          >
            {Array.from({ length: OTP_LENGTH }).map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 55,
                  height: 60,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                  background: themeColors.bigContainer,
                  color: themeColors.text,
                  border: `1px solid ${themeColors.scaffold}`,
                  cursor: "text",
                }}
              >
                {otp[index] ?? ""}
                {index === Math.min(otp.length, OTP_LENGTH - 1) && (
                  <Box
                    component="span"
                    sx={{
                      width: "2px",
                      height: 24,
                      ml: otp[index] ? "2px" : 0,
                      bgcolor: themeColors.text,
                      animation: "otpCaretBlink 1s step-end infinite",
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>

          <input
            ref={otpInputRef}
            autoFocus
            value={otp}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              if (value.length <= OTP_LENGTH) setOtp(value);
            }}
            style={{
              position: "absolute",
              opacity: 0,
              height: 0,
              width: 0,
              border: "none",
            }}
          />

          <Button
            variant="contained"
            fullWidth
            disabled={otp.length !== OTP_LENGTH || verifyingOtp}
            onClick={verifyOtp}
            sx={{
              background: themeColors.primary,
              color: themeColors.text,
              "&:hover": {
                background: themeColors.svgColor,
              },
              border: `1px solid ${themeColors.scaffold}`,
              fontWeight: "bold",
              py: 1.5,
              fontSize: "1rem",
              textTransform: "none",
            }}
          >
            {verifyingOtp ? <CircularProgress size={24} /> : "Verify OTP"}
          </Button>
        </Box>
      )}

      <Dialog
        open={openSheet}
        onClose={() => setOpenSheet(false)}
        fullWidth
        PaperProps={{
          sx: {
            background: themeColors.bigContainer,
            color: themeColors.text,
            border: `1px solid ${themeColors.scaffold}`,
          },
        }}
      >
        <DialogTitle sx={{ color: themeColors.primary }}>
          Select Store
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search store"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                background: themeColors.mediumContainer,
                color: themeColors.text,
              },
              "& .MuiInputLabel-root": {
                color: themeColors.text,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: themeColors.scaffold,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: themeColors.primary,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: themeColors.primary,
              },
            }}
          />

          {filteredStores.map((store) => (
            <Card
              key={store._id}
              sx={{
                padding: 2,
                mb: 1,
                cursor: "pointer",
                background: themeColors.mediumContainer,
                color: themeColors.text,
                "&:hover": {
                  background: themeColors.svgColor,
                },
                border: `1px solid ${themeColors.scaffold}`,
              }}
              onClick={() => {
                setSelectedStore(store);
                setOpenSheet(false);
              }}
            >
              <Typography variant="h6">{store.branchName}</Typography>
              <Typography>Phone: {store.phone}</Typography>
              <Typography>Code: {store.branchCode}</Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography>
                Address:{" "}
                {`${store.address1 || ""} ${store.address2 || ""} ${
                  store.city || ""
                } - ${store.pincode}`}
              </Typography>
            </Card>
          ))}

          {filteredStores.length === 0 && (
            <Typography sx={{ color: themeColors.text }}>
              No stores found.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FromStoreTab;
